import './TemplateDetails.scss';

import { BubbleButton } from '@go-client/components/BubbleUI';
import { BareProperty, PropertyTag } from '@go-shared/types/property';
import { Template } from '@go-shared/types/template';
import React from 'react';

import TemplateForm, { TemplateFormDataBase } from './TemplateForm';
import TemplateAssociations from './TemplateAssociations';
import { BareSailingDay, BareSailing } from '@go-shared/types/sailing';
import { canLinkTemplates } from '@go-shared/lib/permissions';
import { User } from '@go-shared/types/user';

interface TemplateDetailProps {
  property: BareProperty;
  user: User;
  template: Template;
  onUpdatePropertyTags?: (tags: PropertyTag[]) => Promise<unknown>;
  onUpdateTemplate?: (template: Template) => Promise<unknown>;
  onEditCanvas?: () => void;
  onEditFeed?: () => void;
  onSailingDayNameClick?: (sailing: BareSailing, sailingDayId: BareSailingDay) => void;
  onClose?: () => void;
}

const TemplateDetails: React.FC<TemplateDetailProps> = props => {
  async function handleOnUpdateFormTemplateData(templateData: TemplateFormDataBase) {
    if (!props.onUpdateTemplate) return;

    return props.onUpdateTemplate({
      ...props.template,
      name: templateData.name,
      tags: templateData.tags,
      ...(templateData.dayTheme && { dayTheme: templateData.dayTheme }),
    });
  }

  const initialForm = {
    name: props.template.name,
    tags: props.template.tags,
    dayTheme: props.template.dayTheme,
    dirty: true,
  };

  return (
    <div className="template-details p-8">
      <div className="flex items-start mb-5">
        <div>
          <h1 className="text-lg text-slate-dark font-semibold">{props.template.name}</h1>
          <h2 className="text-2xl text-navy">Template Details</h2>
        </div>
        <BubbleButton
          onClick={props.onClose}
          className="ml-auto"
          variant="navy"
          inverse
          icon="xmark-large"
          iconSize="xl"
        />
      </div>
      <div className="grid grid-cols-1 gap-6">
        <div className="jumbo-link relative">
          <div className="jumbo-link-image" />
          <div
            className="absolute top-0 left-0 w-full h-full flex flex-col
         justify-end items-start"
          >
            <div className="flex ml-3 mb-3">
              <BubbleButton
                onClick={props.onEditCanvas}
                className="shadow-md border border-white-darkest canvas-button mr-3"
                variant="white"
                icon={['fass', 'chart-tree-map']}
              >
                Daily Planner
              </BubbleButton>
              <BubbleButton
                onClick={props.onEditFeed}
                className="shadow-md border border-white-darkest canvas-button"
                variant="white"
                icon={['fass', 'newspaper']}
              >
                Feed Editor
              </BubbleButton>
            </div>
          </div>
        </div>
        {canLinkTemplates(props.user) && (
          <div className="bg-white -mx-8 px-8 py-6 border-t border-b">
            <TemplateForm
              initialForm={initialForm}
              propertyTags={props.property.tags}
              onUpdatePropertyTags={async propertyTags => {
                if (!props.onUpdatePropertyTags) return;
                return props.onUpdatePropertyTags(propertyTags);
              }}
              onSubmit={handleOnUpdateFormTemplateData}
            />
          </div>
        )}
        <div>
          <h2 className="text-xl mb-2 text-slate">Future Days Linked to Template</h2>
          <TemplateAssociations
            template={props.template}
            onSailingDayNameClick={props.onSailingDayNameClick}
          />
        </div>
      </div>
    </div>
  );
};

export default TemplateDetails;
